import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  dateFormat?: string;
  numberFormat?: string;
  tier?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  rememberMe: boolean;
}

interface AuthActions {
  login: (token: string, refreshToken: string, user: User, rememberMe: boolean) => void;
  logout: () => void;
  setToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  updateUser: (user: Partial<User>) => void;
  initializeAuth: () => void;
}

// Helper to get appropriate storage based on remember me setting
function getStorage(rememberMe: boolean): Storage {
  return rememberMe ? localStorage : sessionStorage;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      rememberMe: false,

      // Actions
      login: (token, refreshToken, user, rememberMe) => {
        const storage = getStorage(rememberMe);

        // Clear both storages first
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');

        // Save to appropriate storage
        storage.setItem('auth_token', token);
        storage.setItem('refresh_token', refreshToken);
        storage.setItem('user', JSON.stringify(user));

        // Save remember me preference to localStorage
        if (rememberMe) {
          localStorage.setItem('remember_me', 'true');
        } else {
          localStorage.removeItem('remember_me');
        }

        set({
          token,
          refreshToken,
          user,
          isAuthenticated: true,
          rememberMe,
        });
      },

      logout: () => {
        // Clear all auth data from both storages
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('remember_me');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');

        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          rememberMe: false,
        });
      },

      setToken: (token) => {
        const { rememberMe } = get();
        const storage = getStorage(rememberMe);
        storage.setItem('auth_token', token);
        set({ token });
      },

      setRefreshToken: (refreshToken) => {
        const { rememberMe } = get();
        const storage = getStorage(rememberMe);
        storage.setItem('refresh_token', refreshToken);
        set({ refreshToken });
      },

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      initializeAuth: () => {
        // Check both storages for existing auth data
        const rememberMe = localStorage.getItem('remember_me') === 'true';
        const storage = rememberMe ? localStorage : sessionStorage;

        const token = storage.getItem('auth_token');
        const refreshToken = storage.getItem('refresh_token');
        const userStr = storage.getItem('user');

        if (token && userStr) {
          try {
            const user = JSON.parse(userStr);
            set({
              token,
              refreshToken,
              user,
              isAuthenticated: true,
              rememberMe,
            });
          } catch (error) {
            // Invalid user data, clear everything
            get().logout();
          }
        } else {
          // No valid auth data
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            rememberMe: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => {
        // Use a custom storage that checks both localStorage and sessionStorage
        return {
          getItem: (name: string) => {
            const rememberMe = localStorage.getItem('remember_me') === 'true';
            const storage = rememberMe ? localStorage : sessionStorage;
            return storage.getItem(name);
          },
          setItem: (name: string, value: string) => {
            const rememberMe = localStorage.getItem('remember_me') === 'true';
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem(name, value);
          },
          removeItem: (name: string) => {
            localStorage.removeItem(name);
            sessionStorage.removeItem(name);
          },
        };
      }),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
    }
  )
);
